import { DebounceInput } from '@components/form/input/debounce-input';
import { ScrollableDiv } from '@components/scrollable-div';
import { useSubmenuMembersList } from '@modules/members/hooks/use-submenu-members-list';
import { Empty, Menu, Skeleton } from 'antd';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import { RiSearchLine } from 'react-icons/ri';
import { MemberCard } from '../sidebar-members/member-card';
import { FilterMembers } from './filter-members';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLayoutContext } from '@/container/context/layout/layout-context';
import { SortMemberType } from './sort-member-type';
import { useTranslation } from 'react-i18next';
import { capitalize, capitalizeFirst } from '@shared/utils/capitalize';
import { AllMembersCard } from '../sidebar-members/all-members-card';
import {
  ALL_MEMBERS_KEY,
  ALL_MEMBERS_ROUTE,
} from '@modules/conversations/features/all-members-conversation/all-members-conversation.constants';

export default function SubmenuMembersView() {
  const navigate = useNavigate();
  const { memberId = '' } = useParams();
  const { pathname } = useLocation();
  const { closeSidebarMobileDrawer } = useLayoutContext();
  const { data, loading, loadNextRef, hasNextPage, initialLoading, filter, setFilter, handleSortMember, allMembers } =
    useSubmenuMembersList();
  const { t } = useTranslation();

  const memberItems: ItemType<MenuItemType>[] =
    data?.data?.map((member) => ({
      type: 'item',
      key: String(member?.id),
      className: 'group py-8 !px-2 group !overflow-visible',
      label: <MemberCard {...member} />,
      onClick: () => {
        closeSidebarMobileDrawer();
        navigate({
          pathname: '/conversations/member/' + member?.id,
        });
      },
    })) ?? [];

  const items: ItemType<MenuItemType>[] = filter.query
    ? memberItems
    : [
        {
          type: 'item',
          key: ALL_MEMBERS_KEY,
          className: 'group py-8 !px-2 group !overflow-visible',
          label: (
            <AllMembersCard
              isOnline={!!data?.data?.some((m) => m?.userOnlineManagement?.isActive)}
              chat={allMembers?.chat}
              unreadCount={allMembers?.unreadCounter}
            />
          ),
          onClick: () => {
            closeSidebarMobileDrawer();
            navigate({ pathname: ALL_MEMBERS_ROUTE });
          },
        },
        ...memberItems,
      ];

  const loadMoreSkeleton =
    initialLoading || hasNextPage ? (
      <div ref={loadNextRef}>
        <Skeleton active paragraph={false} />
      </div>
    ) : undefined;
  return (
    <>
      <div className="px-2.5 pt-3 space-y-2">
        <DebounceInput
          allowClear
          size="large"
          prefix={<RiSearchLine />}
          placeholder={capitalizeFirst(t('find_member')) + '..'}
          defaultValue={filter.query}
          setDebouncedValue={(v) => setFilter((prev) => ({ ...prev, query: v }))}
        />
        <section className="flex items-center justify-between">
          <FilterMembers setFilter={setFilter} />
          <SortMemberType onChange={handleSortMember} />
        </section>
      </div>
      <ScrollableDiv className="custom-sidebar-menu pt-2">
        {items.length || loading ? (
          <Menu
            selectedKeys={[pathname.startsWith(ALL_MEMBERS_ROUTE) ? ALL_MEMBERS_KEY : String(memberId)]}
            style={{ height: 20, background: 'transparent' }}
            mode="inline"
            className="h-full border-0! custom-menu"
            items={[
              ...items,
              {
                key: 'lazy-loader',
                style: { display: initialLoading || hasNextPage ? 'block' : 'none' },
                label: loadMoreSkeleton,
              },
            ]}
          />
        ) : (
          <Empty description={capitalize(t('no_members_found')) + '...'} className="mt-4" />
        )}
      </ScrollableDiv>
    </>
  );
}
